<template>
  <div class="bar-chart-container pt-5 pr-5">
    <Bar
      :chart-options="computedOptions"
      :chart-data="computedChartData"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="computedPlugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script>
import { Bar } from "vue-chartjs/legacy";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ChartDataLabels
);

export default {
  name: "BaseBarChart",
  components: { Bar },
  props: {
    chartId: {
      type: String,
      default: "bar-chart",
    },
    datasetIdKey: {
      type: String,
      default: "label",
    },
    width: {
      type: Number,
      default: 400,
    },
    height: {
      type: Number,
      default: 400,
    },
    cssClasses: {
      default: "",
      type: String,
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    chartData: {
      type: Object,
      required: true,
    },
    chartOptions: {
      type: Object,
      default: () => ({}),
    },
    enableDataLabels: {
      type: Boolean,
      default: false,
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    mainsTotalPassed: {
      type: Number,
      default: null,
    },
    mainsTotalFailed: {
      type: Number,
      default: null,
    },
    screeningTotalPassed: {
      type: Number,
      default: null,
    },
    screeningTotalFailed: {
      type: Number,
      default: null,
    },
    demoTotalCompletion: {
      type: Number,
      default: null,
    },
    activeTab: {
      type: Number,
      default: 0,
    }
  },

  computed: {
    formattedLabels() {
      const labels = this.chartData.labels || [];
      
      // Only show "Week X" if type === 1
      if (this.activeTab === 1) {
        return labels.map((label, index) => {
          
          return [label, `Week ${index + 1}`]; // index 1 = "Week 1", index 2 = "Week 2", etc.
        });
      }
      // If type !== 1, return labels as-is
      return labels;
    },

    computedChartData() {
      return {
        ...this.chartData,
        labels: this.formattedLabels
      };
    },

    computedPlugins() {
      return this.enableDataLabels ? [ChartDataLabels] : [];
    },

    computedOptions() {
      const baseOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              font: {
                size: 14,
              },
              usePointStyle: true,
              pointStyle: "rect",
              generateLabels: this.generateCustomLabels,
            },
          },
          datalabels: this.enableDataLabels
            ? {
                display: true,
                anchor: this.stacked ? "center" : "end",
                align: this.stacked ? "center" : "top",
                color: "#000",
                font: {
                  size: 12,
                },
                formatter: (value) => {
                  if (value == null || value === 0) return "";
                  return value;
                },
              }
            : { display: false },
        },
        scales: {
          x: {
            stacked: this.stacked,
            grid: { display: false },
            ticks: {
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0,
              font: {
                size: 11,
              }
            },
          },
          y: {
            stacked: this.stacked,
            beginAtZero: true,
            grid: { display: false },
             grace: '10%',
          },
        },
      };
      return { ...baseOptions, ...this.chartOptions };
    },
  },

  methods: {
    generateCustomLabels(chart) {
      const datasets = chart.data.datasets;
      
      return datasets.map((dataset, i) => {
        let count = null;
        let hasCount = false;
        
        if (dataset.label === 'Pass') {
          if (this.screeningTotalPassed !== null && this.screeningTotalPassed !== undefined) {
            count = this.screeningTotalPassed;
            hasCount = true;
          }
          if (this.mainsTotalPassed !== null && this.mainsTotalPassed !== undefined) {
            count = this.mainsTotalPassed;
            hasCount = true;
          }
        } else if (dataset.label === 'Fail') {
          if (this.screeningTotalFailed !== null && this.screeningTotalFailed !== undefined) {
            count = this.screeningTotalFailed;
            hasCount = true;
          }
          if (this.mainsTotalFailed !== null && this.mainsTotalFailed !== undefined) {
            count = this.mainsTotalFailed;
            hasCount = true;
          }
        } else if (dataset.label === 'Demo Completed') {
          if (this.demoTotalCompletion !== null && this.demoTotalCompletion !== undefined) {
            count = this.demoTotalCompletion;
            hasCount = true;
          }
        }

        return {
          text: hasCount ? `${dataset.label} (${count})` : dataset.label,
          fillStyle: dataset.backgroundColor,
          strokeStyle: dataset.backgroundColor,
          lineWidth: 2,
          hidden: false,
          index: i,
          pointStyle: 'rect'
        };
      });
    },

    forceChartUpdate() {
      this.$forceUpdate();
    }
  },

  watch: {
    chartData: {
      deep: true,
      handler() {
        this.forceChartUpdate();
      },
    },
    chartOptions: {
      deep: true,
      handler() {
        this.forceChartUpdate();
      },
    },
    mainsTotalPassed() {
      this.forceChartUpdate();
    },
    mainsTotalFailed() {
      this.forceChartUpdate();
    },
    screeningTotalPassed() {
      this.forceChartUpdate();
    },
    screeningTotalFailed() {
      this.forceChartUpdate();
    },
    demoTotalCompletion() {
      this.forceChartUpdate();
    },
    activeTab() {
      this.forceChartUpdate();
    }
  },
};
</script>

<style scoped>
.bar-chart-container {
  width: 100%;
  height: 100%;
  max-width: 100%;
  margin: auto;
  position: relative;
}
.bar-chart-container canvas {
  height: 280px !important;
}
</style>